import { Controller } from "@hotwired/stimulus"

// Adds a "Download CSV" button above every table in a rendered custom report. Exports
// the cells as displayed (formatted money, author-chosen columns), which covers both
// {% table %} tags and author-written <table> markup alike. Files are named for the
// report plus the table's section: the nearest heading preceding it.
export default class extends Controller {
  static values = { name: { default: "report", type: String } }

  connect() {
    const headings = Array.from(this.element.querySelectorAll("h1, h2, h3, h4, h5, h6"))
    this.element.querySelectorAll("table").forEach((table, index) => {
      if (table.previousElementSibling?.dataset.reportTableCsvButton) return

      const bar = document.createElement("div")
      bar.className = "flex justify-end print:hidden"
      bar.dataset.reportTableCsvButton = "true"

      const button = document.createElement("button")
      button.className = "cursor-pointer font-medium hover:text-blue-800 text-blue-600 text-xs"
      button.textContent = "Download CSV"
      button.dataset.action = "report-table-csv#download"
      button.dataset.reportTableCsvIndexParam = index
      button.dataset.reportTableCsvSectionParam = this.sectionName({ headings, table })
      button.type = "button"

      bar.appendChild(button)
      table.before(bar)
    })
  }

  csvCell(value) {
    // Spreadsheets evaluate a leading = + - @ as a formula; neutralize it, sparing
    // negative numbers and amounts so they still import as numbers.
    const numeric = /^-?\$?[\d,]*\.?\d+$/.test(value)
    const safe = !numeric && /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
  }

  download({ currentTarget, params: { index, section } }) {
    const table = currentTarget.parentElement.nextElementSibling
    const csv = Array.from(table.rows)
      .map((row) => Array.from(row.cells).map((cell) => this.csvCell(cell.textContent.trim())).join(","))
      .join("\r\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.download = `${this.slug(this.nameValue) || "report"}-${this.slug(section) || `table-${index + 1}`}.csv`
    link.href = url
    link.click()
    URL.revokeObjectURL(url)
  }

  sectionName({ headings, table }) {
    const preceding = headings.filter((heading) => heading.compareDocumentPosition(table) & Node.DOCUMENT_POSITION_FOLLOWING)
    return preceding.at(-1)?.textContent.trim() ?? ""
  }

  slug(text) {
    return String(text).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
  }
}
